import { getToken } from '../services/auth';
/**
 * Utility functions for file download operations
 */

export interface DownloadOptions {
  filename?: string;
  showProgress?: boolean;
  onProgress?: (progress: number) => void;
  onError?: (error: string) => void;
  onSuccess?: () => void;
}

/**
 * Downloads a file by navigating to the s3-download endpoint which returns
 * a 302 redirect to S3 with Content-Disposition: attachment, forcing the
 * browser to save the file instead of displaying it.
 *
 * @param s3Key  The S3 object key (e.g. "docs/SHP-001/abc/photo.jpg")
 * @param filename  Display filename (used only as fallback)
 */
export const downloadFile = async (
  s3Key: string,
  filename: string,
  _options: DownloadOptions = {}
): Promise<void> => {
  const link = document.createElement('a');
  // The download needs the sign-in: a plain link cannot send it as a header.
  const token = getToken() || '';
  link.href = `/.netlify/functions/s3-download?key=${encodeURIComponent(s3Key)}${token ? `&t=${encodeURIComponent(token)}` : ''}`;
  link.download = filename;
  link.style.display = 'none';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/**
 * Downloads all files for a shipment as a ZIP archive
 */
export const downloadAllFilesAsZip = async (
  shipmentId: string | null,
  token: string,
  options: DownloadOptions = {}
): Promise<void> => {
  try {
    // Determine the endpoint based on whether a specific shipment is selected
    const endpoint = shipmentId 
      ? `/.netlify/functions/download-all-files/shipment/${shipmentId}`
      : `/.netlify/functions/download-all-files`;

    const response = await fetch(endpoint, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || 'Failed to download files');
    }

    // Get the zip file as blob
    const blob = await response.blob();
    console.log('Download blob created:', blob.size, 'bytes');
    
    // Create download link
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Set filename based on what we're downloading
    const filename = shipmentId 
      ? `${shipmentId}_files.zip`
      : `my_files_${new Date().toISOString().split('T')[0]}.zip`;
    
    link.download = filename;
    link.style.display = 'none'; // Hide the link
    
    console.log('Triggering download:', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
    
    console.log('Download triggered successfully');

    options.onSuccess?.();
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : 'Download failed';
    options.onError?.(errorMessage);
    throw error;
  }
};

/**
 * Formats file size in human-readable format
 */
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

/**
 * Gets file extension from filename
 */
export const getFileExtension = (filename: string): string => {
  return filename.split('.').pop()?.toLowerCase() || '';
};

/**
 * Checks if a file is an image based on its extension
 */
export const isImageFile = (filename: string): boolean => {
  const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg'];
  return imageExtensions.includes(getFileExtension(filename));
};

/**
 * Checks if a file is a PDF based on its extension
 */
export const isPdfFile = (filename: string): boolean => {
  return getFileExtension(filename) === 'pdf';
};

/**
 * Checks if a file is a document based on its extension
 */
export const isDocumentFile = (filename: string): boolean => {
  const documentExtensions = ['doc', 'docx', 'pdf', 'txt', 'rtf'];
  return documentExtensions.includes(getFileExtension(filename));
};

/**
 * Checks if a file is a spreadsheet based on its extension
 */
export const isSpreadsheetFile = (filename: string): boolean => {
  const spreadsheetExtensions = ['xls', 'xlsx', 'csv'];
  return spreadsheetExtensions.includes(getFileExtension(filename));
};

/**
 * Gets appropriate icon name for file type
 */
export const getFileIconName = (filename: string): string => {
  if (isImageFile(filename)) return 'image';
  if (isPdfFile(filename)) return 'picture_as_pdf';
  if (isDocumentFile(filename)) return 'description';
  if (isSpreadsheetFile(filename)) return 'table_chart';
  return 'insert_drive_file';
};

/**
 * Validates file type against allowed types
 */
export const validateFileType = (file: File, allowedTypes: string[]): boolean => {
  return allowedTypes.includes(file.type);
};

/**
 * Validates file size against maximum size
 */
export const validateFileSize = (file: File, maxSizeInMB: number): boolean => {
  const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
  return file.size <= maxSizeInBytes;
};

/**
 * Generates a unique filename to avoid conflicts
 */
export const generateUniqueFilename = (originalFilename: string): string => {
  const timestamp = Date.now();
  const extension = getFileExtension(originalFilename);
  const nameWithoutExtension = originalFilename.replace(/\.[^/.]+$/, '');
  return `${nameWithoutExtension}_${timestamp}.${extension}`;
};

/**
 * Sanitizes filename by removing invalid characters
 */
export const sanitizeFilename = (filename: string): string => {
  return filename.replace(/[<>:"/\\|?*]/g, '_');
};

export interface FileNamingContext {
  shipmentId?: string;
  label?: string;
  fileIndex?: number;  // 1-based
  totalFiles?: number;
}

const sanitizeLabel = (label: string): string =>
  label
    .replace(/[^a-zA-Z0-9 _-]/g, '')
    .trim()
    .replace(/\s+/g, '_')
    .substring(0, 40);

/**
 * Produces a professional filename for uploads.
 *
 * When `context` is supplied the output follows the pattern:
 *   {shipmentId}_{label}_{NNN}.ext
 *   e.g. "SALLDKHS88A753595_Scheduled_001.jpg"
 *
 * When no context is given, messaging-app names are cleaned up:
 *   "WhatsApp Image 2026-03-06 at 10.15.23.jpeg"  → "Photo_2026-03-06_101523.jpeg"
 *   "IMG-20260306-WA0001.jpg"                      → "IMG_20260306_0001.jpg"
 *   Non-matching filenames are returned unchanged.
 */
export const cleanUploadFilename = (filename: string, context?: FileNamingContext): string => {
  const dotIdx = filename.lastIndexOf('.');
  const ext = dotIdx >= 0 ? filename.substring(dotIdx) : '';

  if (context?.shipmentId) {
    const pad = String(context.totalFiles ?? 1).length;
    const num = String(context.fileIndex ?? 1).padStart(Math.max(pad, 3), '0');
    const label = context.label ? `_${sanitizeLabel(context.label)}` : '';
    return `${context.shipmentId}${label}_${num}${ext}`;
  }

  const nameWithoutExt = dotIdx >= 0 ? filename.substring(0, dotIdx) : filename;

  // "WhatsApp Image 2026-03-06 at 10.15.23 (2)" style
  const whatsappLong = nameWithoutExt.match(
    /^WhatsApp\s+(Image|Video|Document|Audio)\s+(\d{4}-\d{2}-\d{2})\s+at\s+(\d{2})\.(\d{2})\.(\d{2})(?:\s*\((\d+)\))?$/i
  );
  if (whatsappLong) {
    const typeMap: Record<string, string> = { image: 'Photo', video: 'Video', document: 'Document', audio: 'Audio' };
    const type = typeMap[whatsappLong[1].toLowerCase()] || whatsappLong[1];
    const date = whatsappLong[2];
    const time = `${whatsappLong[3]}${whatsappLong[4]}${whatsappLong[5]}`;
    const suffix = whatsappLong[6] ? `_${whatsappLong[6]}` : '';
    return `${type}_${date}_${time}${suffix}${ext}`;
  }

  // "IMG-20260306-WA0001" / "VID-20260306-WA0003" style
  const whatsappShort = nameWithoutExt.match(
    /^(IMG|VID|AUD|DOC|PTT)-(\d{8})-WA(\d+)$/i
  );
  if (whatsappShort) {
    return `${whatsappShort[1].toUpperCase()}_${whatsappShort[2]}_${whatsappShort[3]}${ext}`;
  }

  return filename;
};
